import type { LatLon } from '@/libs/journey/journeys';

/** IGN Géoplateforme pedestrian routing: free, keyless, France only. */
const ROUTE_URL = 'https://data.geopf.fr/navigation/itineraire';
const MAX_CONCURRENT_REQUESTS = 6;
const CACHE_SIZE = 200;
/** A walk the service found no path for is not asked again before this, however often the journeys refresh. */
const NO_PATH_RETRY_MS = 10 * 60 * 1000;
/** A stalled request gives up by itself: it would otherwise hold its slot and keep the walk from being asked again. */
const WALK_REQUEST_TIMEOUT_MS = 10000;
/**
 * Time given to the service before the journeys are shown with the walks it has measured so far.
 * Kept for the V1 (#278), to tune after trials on a real network.
 */
export const WALK_BUDGET_MS = 2500;

/** A walk measured along the streets. */
export interface WalkQuote {
  distance: number;
  seconds: number;
  /** [lon, lat] points of the walk. */
  path: Array<[number, number]>;
}

export interface WalkRequest {
  key: string;
  from: LatLon;
  to: LatLon;
}

interface RouteResponse {
  distance?: number;
  duration?: number;
  geometry?: { coordinates?: Array<[number, number]> };
}

/** The walk as the IGN measures it: its own duration, not the 15 min/km pace of the crow-fly estimate. */
export function parseRouteResponse(body: RouteResponse): WalkQuote | undefined {
  const path = body.geometry?.coordinates;
  if (typeof body.distance !== 'number' || typeof body.duration !== 'number') return undefined;
  if (!path || path.length < 2) return undefined;
  return { distance: body.distance, seconds: Math.round(body.duration), path };
}

const cache = new Map<string, WalkQuote>();
const inFlight = new Map<string, Promise<WalkQuote | undefined>>();
const noPathSince = new Map<string, number>();

const hasNoPath = (key: string) => Date.now() - (noPathSince.get(key) ?? -Infinity) < NO_PATH_RETRY_MS;

function remember(key: string, quote: WalkQuote) {
  cache.delete(key);
  cache.set(key, quote);
  if (cache.size > CACHE_SIZE) cache.delete(cache.keys().next().value!);
}

let running = 0;
const waiting: Array<() => void> = [];

async function limited<T>(task: () => Promise<T>): Promise<T> {
  if (running >= MAX_CONCURRENT_REQUESTS) await new Promise<void>(resolve => waiting.push(resolve));
  running++;
  try {
    return await task();
  } finally {
    running--;
    waiting.shift()?.();
  }
}

/** The walk along the streets, or undefined where the service finds none (outside France). */
async function requestWalk({ from, to }: WalkRequest): Promise<WalkQuote | undefined> {
  const params = new URLSearchParams({
    resource: 'bdtopo-valhalla',
    profile: 'pedestrian',
    optimization: 'shortest',
    start: `${from.lon},${from.lat}`,
    end: `${to.lon},${to.lat}`,
    geometryFormat: 'geojson',
    distanceUnit: 'meter',
    timeUnit: 'second',
    getSteps: 'false',
  });
  // eslint-disable-next-line compat/compat -- iOS 11 is below our real support floor (plugin-legacy targets 'defaults, not IE 11'); .browserslistrc is intentionally broader.
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), WALK_REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(`${ROUTE_URL}?${params}`, { signal: controller.signal });
    if (response.status === 404) return undefined;
    if (!response.ok)
      throw new Error(`${response.status}: ${response.statusText} when trying to get ${ROUTE_URL}`);
    return parseRouteResponse(await response.json());
  } finally {
    clearTimeout(timeout);
  }
}

function fetchWalk(request: WalkRequest): Promise<WalkQuote | undefined> {
  const pending = inFlight.get(request.key);
  if (pending) return pending;
  const promise = limited(() => requestWalk(request))
    .then(quote => {
      if (quote) remember(request.key, quote);
      else noPathSince.set(request.key, Date.now());
      return quote;
    })
    .finally(() => inFlight.delete(request.key));
  inFlight.set(request.key, promise);
  return promise;
}

/**
 * The walks measured within the budget, cached ones first. A walk the service does
 * not answer in time keeps its estimate; its answer still lands in the cache for the
 * next search.
 */
export async function fetchWalks(
  requests: WalkRequest[],
  budgetMs: number = WALK_BUDGET_MS,
): Promise<Map<string, WalkQuote>> {
  const quotes = new Map<string, WalkQuote>();
  const pending: Array<Promise<unknown>> = [];
  // Offline, the estimates are all there is: no need to wait for requests bound to fail.
  const offline = navigator.onLine === false;
  requests.forEach(request => {
    const cached = cache.get(request.key);
    if (cached) {
      quotes.set(request.key, cached);
      return;
    }
    if (offline || hasNoPath(request.key)) return;
    pending.push(
      fetchWalk(request)
        .then(quote => quote && quotes.set(request.key, quote))
        .catch(error => {
          // A stalled connection given up, like a dropped one: the walk keeps its estimate, nothing to act on.
          if (error?.name !== 'AbortError') console.warn('Walk routing failed:', error);
        }),
    );
  });
  if (pending.length > 0) {
    let timer: number | undefined;
    const budget = new Promise<void>(resolve => (timer = window.setTimeout(resolve, budgetMs)));
    await Promise.race([Promise.all(pending), budget]);
    window.clearTimeout(timer);
  }
  return new Map(quotes);
}

export function clearWalkCache() {
  cache.clear();
  noPathSince.clear();
}
